import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { View, Keyboard, Platform } from 'react-native';
import { AnimatedFlashList } from '@shopify/flash-list';
import Spacer from 'design-system/Spacer';
import { CardView } from 'design-system/CardView';
import Indicator from 'design-system/Indicator';
import { SCROLL_INDICATOR_INSETS, rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import ListEmptyView from 'design-system/ListEmptyView';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useReSelector } from 'utils/hooksApi';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useFetchOld } from 'hooks';
import StackHeader from 'design-system/hoc/StackHeader';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import Modal from 'utils/packages/Modal';
import useNavigateTransactionItem from 'features/transactions/hooks/useNavigateTransactionItem';
import useColorScheme from 'hooks/useColorScheme';
import Animated from 'react-native-reanimated';
import WalkthroughHeaderOverlay from 'features/feed/components/walkthrough/WalkthroughHeaderOverlay';
import { useIsMainAppWalkthroughV6Active } from 'features/feed/components/walkthrough/hooks/useIsMainAppWalkthroughV6Active';
import useWalkthroughLayout from 'features/feed/components/walkthrough/useWalkthroughLayout';
import Walkthrough from 'features/feed/components/walkthrough/Walkthrough';
import { useShowWalkthroughV6 } from 'features/feed/components/walkthrough/hooks/useShowWalkthroughV6';
import { IconSettings } from 'design-system/icons/IconSettings';
import Amplitude from '../../../utils/amplitude';
import { makeSelectSubscriptionById } from '../selectors';
import { useSubscriptionMenu } from '../hooks/useSubscriptionMenu';
import { editSubscription, getSubscriptionsById, removeTxnsFromSubscription, } from '../actions';
import { SubscriptionScreenContext } from '../context/subscriptionScreenContext';
import SubscriptionScreenListHeader from '../components/SubscriptionScreenListHeader';
import RowSubscriptionTransaction, { ROW_SUB_TRANSACTION_HEIGHT, } from '../components/transactions/RowSubscriptionTransaction';
import { isWeb } from '../../../constants';
const keyExtractor = (item) => item.id.toString();
const SubscriptionScreen = withAnimatedNavigationHeader(({ navigation, route, onScroll }) => {
    const touchViewRef = useRef(null);
    const colors = useColors();
    const colorScheme = useColorScheme();
    const dispatch = useAppDispatch();
    const navigateToTransactionItem = useNavigateTransactionItem();
    const styles = useStyles(styleSet);
    const showWalkthroughV6 = useShowWalkthroughV6();
    const isMainWalkthroughActive = useIsMainAppWalkthroughV6Active();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.listContent, paddingHorizontalStyle);
    useEffect(() => {
        if (route.params?.id) {
            dispatch(getSubscriptionsById(route.params.id));
        }
    }, [route.params?.id]);
    const [, , , fetch] = useFetchOld({
        throws: true,
        muteError: false,
        muteFetching: false,
    });
    const [editing, setIsEditing] = useState(false);
    const [txnToDelete, setTxnToDelete] = useState([]);
    const [isCancellingPayment, setIsCancellingPayment] = useState(false);
    const memoSelectSubscriptionById = useMemo(makeSelectSubscriptionById, []);
    const subscription = useAppSelector((store) => memoSelectSubscriptionById(store, route.params.id));
    const subscriptionTransactions = useReSelector((store) => store.subscriptions.subscriptionTransactions);
    const isFetching = useAppSelector((store) => store.subscriptions.isFetchingSubscriptionsTransactions);
    const listToShow = useMemo(() => {
        if (!txnToDelete.length)
            return subscriptionTransactions;
        return subscriptionTransactions.filter((txn) => !txnToDelete.includes(txn.id));
    }, [subscriptionTransactions, txnToDelete]);
    const [openSubscriptionMenu] = useSubscriptionMenu(navigation);
    const showOptions = useCallback((data) => {
        Keyboard.dismiss();
        openSubscriptionMenu(subscription, data, touchViewRef);
        Amplitude.logEvent('Subscription.OpenMenu');
    }, [openSubscriptionMenu, subscription]);
    useLayoutEffect(() => {
        const headerTitle = subscription?.customName || subscription?.merchantInfo?.displayName;
        navigation.setOptions({
            headerRight: () => (<View ref={touchViewRef}>
            <NavigationHeaderRightIconButton onPress={() => {
                    showOptions();
                }} disabled={isMainWalkthroughActive}>
              <IconSettings color={isMainWalkthroughActive
                    ? colors.elements.primaryOnModal
                    : colors.elements.primary}/>
            </NavigationHeaderRightIconButton>
          </View>),
            headerTitle: () => (<StackHeader color={isMainWalkthroughActive
                    ? colors.elements.primaryOnModal
                    : undefined} title={headerTitle || ''}/>),
            title: isWeb ? headerTitle : undefined,
        });
    }, [
        showOptions,
        colors.elements.primary,
        colors.elements.primaryOnModal,
        subscription?.customName,
        subscription?.merchantInfo?.displayName,
        isMainWalkthroughActive,
    ]);
    useEffect(() => {
        navigation.setParams({
            headerTintColor: isMainWalkthroughActive
                ? colors.elements.primaryOnModal
                : undefined,
        });
    }, [colors.elements.primaryOnModal, isMainWalkthroughActive]);
    const { itemPositions, scrollHeight, scrollLayoutHeight } = useWalkthroughLayout();
    const editPrediction = useCallback(() => {
        if (subscription) {
            navigation.navigateDeprecated('AdjustPrediction', {
                id: subscription.id,
            });
        }
    }, [subscription]);
    const addPrediction = useCallback(() => {
        if (subscription) {
            navigation.navigateDeprecated('AdjustPrediction', {
                id: subscription.id,
                isCreate: true,
            });
        }
    }, [subscription]);
    const onCancelPayment = useCallback(() => {
        if (!subscription?.id)
            return;
        const { id } = subscription;
        Modal.showConfirmation({
            title: 'Deactivate this payment?',
            subtitle: `This will mark the recurring ${subscription.isIncome ? 'income' : 'payment'} as inactive and we won't track it anymore. You can reactivate it later if needed.`,
            confirmButtonTitle: 'No, go back',
            cancelButtonTitle: 'Yes, deactivate',
            onConfirm: () => Modal.hide(),
            onPressCancel: async () => {
                Modal.hide();
                setIsCancellingPayment(true);
                try {
                    await fetch(editSubscription(id, { isActive: false }));
                }
                finally {
                    setIsCancellingPayment(false);
                }
            },
        });
    }, [fetch, subscription]);
    const onSkipPayment = useCallback(() => {
        if (!subscription)
            return;
        Modal.showConfirmation({
            title: 'Paying later?',
            subtitle: `Let's update the expected${subscription.isIncome ? ' ' : ' due '}date so your tracking stays accurate`,
            confirmButtonTitle: 'No, go back',
            cancelButtonTitle: `Update${subscription.isIncome ? ' ' : ' due '}date`,
            onConfirm: () => Modal.hide(),
            onPressCancel: () => {
                Modal.hide();
                if (subscription.predictions?.[0]?.date) {
                    editPrediction();
                }
                else {
                    addPrediction();
                }
            },
        });
    }, [subscription, editPrediction, addPrediction]);
    const onAlreadyPaid = useCallback(() => {
        if (!subscription)
            return;
        navigation.push('SubscriptionsSearch', {
            subscriptionId: subscription.id,
            resolveMissedPayment: true,
            income: subscription.isIncome,
            empty: {
                title: 'Find the transaction',
                subtitle: 'Search through your transactions and select the missing payment, so we can keep your tracking up to date',
            },
        });
    }, [subscription]);
    const onEdit = useCallback(() => {
        if (!subscription?.id)
            return;
        setIsEditing(true);
    }, [subscription?.id]);
    const onDone = useCallback(async () => {
        if (!subscription?.id)
            return;
        setIsEditing(false);
        if (!txnToDelete.length)
            return;
        try {
            await fetch(removeTxnsFromSubscription(subscription?.id, txnToDelete));
            setTxnToDelete([]);
        }
        catch {
            setTxnToDelete([]);
        }
    }, [subscription?.id, txnToDelete]);
    const addTxnToDelete = useCallback((txnId) => {
        setTxnToDelete((txnIds) => [...txnIds, txnId]);
    }, []);
    const ListHeader = useMemo(() => (<SubscriptionScreenListHeader onDone={onDone} onEdit={onEdit} editPrediction={editPrediction} addPrediction={addPrediction} onCancel={onCancelPayment} onSkip={onSkipPayment} onAlreadyPaid={onAlreadyPaid} isCancelling={isCancellingPayment}/>), [
        addPrediction,
        editPrediction,
        onDone,
        onEdit,
        onCancelPayment,
        onSkipPayment,
        onAlreadyPaid,
        isCancellingPayment,
    ]);
    const openTransaction = useCallback((item) => {
        const itemWithSubscriptionId = {
            ...item,
            subscriptionId: subscription?.id || null,
        };
        navigateToTransactionItem(itemWithSubscriptionId, false, {
            skipGetSubscription: true,
        });
    }, [navigateToTransactionItem, subscription?.id]);
    const renderItem = useCallback(({ item, index }) => (<View style={[
            styles.renderItem,
            index === 0 &&
                (editing || !subscription?.isActive) &&
                styles.itemContainerFirst,
            index === listToShow.length - 1 && styles.itemContainerLast,
        ]}>
          <RowSubscriptionTransaction data={item} canEdit isEditing={editing} onPress={openTransaction} addTxnToDelete={addTxnToDelete} isIncome={subscription?.isIncome}/>
        </View>), [
        styles.renderItem,
        styles.itemContainerFirst,
        styles.itemContainerLast,
        editing,
        subscription?.isActive,
        subscription?.isIncome,
        listToShow.length,
        openTransaction,
        addTxnToDelete,
    ]);
    const renderListEmpty = useCallback(() => {
        if (isFetching) {
            return null;
        }
        return (<>
          {!editing && <Spacer h={16}/>}
          <CardView noMarginTop>
            <ListEmptyView compact title="No transactions" style={styles.listEmptyView} subTitle="All of your transactions will appear here."/>
          </CardView>
        </>);
    }, [editing, isFetching, styles.listEmptyView]);
    const getItemLayout = useCallback((_data, index) => ({
        index,
        length: ROW_SUB_TRANSACTION_HEIGHT,
        offset: ROW_SUB_TRANSACTION_HEIGHT * index,
    }), []);
    const contextValue = useMemo(() => ({
        editing,
        isFetching,
        data: subscription,
        hasData: !!subscriptionTransactions.length,
        name: subscription?.customName || subscription?.merchantInfo?.displayName,
    }), [editing, isFetching, subscription, subscriptionTransactions.length]);
    // to force re-render rows
    const extraData = useMemo(() => [editing, colorScheme], [editing, colorScheme]);
    const listProps = useMemo(() => ({
        data: listToShow,
        extraData,
        onScroll,
        renderItem,
        scrollEventThrottle: 16,
        keyExtractor,
        ListHeaderComponent: ListHeader,
        keyboardShouldPersistTaps: 'handled',
        ListEmptyComponent: renderListEmpty,
        contentContainerStyle: paddingBottom,
        showsVerticalScrollIndicator: true,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
    }), [
        ListHeader,
        extraData,
        listToShow,
        onScroll,
        paddingBottom,
        renderItem,
        renderListEmpty,
    ]);
    return (<AppView withFrame={false}>
        <SubscriptionScreenContext.Provider value={contextValue}>
          <View style={styles.content}>
            <StatusBar barStyle="dark-content" animated/>
            {/* eslint-disable-next-line no-nested-ternary */}
            {isFetching &&
            !(subscription?.maxAmount && subscription?.minAmount) &&
            !subscription?.transactions?.length ? (<View style={styles.indicatorContainer}>
                <Indicator dark/>
              </View>) : isWeb ? (<Animated.FlatList {...listProps} initialNumToRender={4} getItemLayout={getItemLayout}/>) : (<AnimatedFlashList {...listProps}/>)}
          </View>
        </SubscriptionScreenContext.Provider>
        {!!showWalkthroughV6 && (<Walkthrough scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>)}
      </AppView>);
}, ({ colors, route }) => ({
    Overlay: () => <WalkthroughHeaderOverlay v6/>,
    hasCustomTitle: true,
    headerTintColor: route.params?.headerTintColor,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    content: {
        flex: 1,
    },
    listEmptyView: {
        paddingVertical: rem(24),
    },
    listContent: {
        paddingTop: rem(24),
    },
    renderItem: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && {
            padding: rem(8),
        }),
    },
    itemContainerFirst: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
    },
    itemContainerLast: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(Platform.OS === 'ios' && {
            overflow: 'hidden',
        }),
    },
    indicatorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
}));
export default SubscriptionScreen;
